import * as ko from 'knockout';
import LZString from 'lz-string';
import { ppFetch } from '../shared/pp-fetch';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { CfbTeam } from './models/team';
import { CfbGame } from './models/game';
import { CfbWeek } from './models/week';
import { CfbGameType } from './models/game-type';
import { CfbDraftTrade } from './models/draft-trade';
import { SelectionType } from '../shared/enums/selection-type';
import { RankMethod } from '../shared/enums/rank-method';
import { Enumerable } from '../shared/linq';
import { userConfigManager } from '../shared/user-config-manager';
import { readConfig, getApimBaseUrl } from '../shared/config';
import type { IGame } from '../shared/interfaces/IGame';
import type { ITeam } from '../shared/interfaces/ITeam';
import type { AppConfig } from '../shared/config';
import * as Information from './schedule-info';
import * as BracketGenerator from './bracket-generator';
import { mapDictionaryToArray, rankSort } from '../shared/helpers';

// Re-export for entry point use
export { CfbApp };

// Expose static helpers on window for Knockout template bindings in Razor views
function exposeToWindow(app: CfbApp): void {
  (window as any).SchedulePicker = (window as any).SchedulePicker ?? {};
  (window as any).SchedulePicker.CFB = (window as any).SchedulePicker.CFB ?? {};
  (window as any).SchedulePicker.CFB.App = {
    mapDictionaryToArray: mapDictionaryToArray,
    RankSort: rankSort,
  };
  (window as any).vm = app;
}

interface ICfbGameData {
  Home: any;
  Away: any;
  Week: number;
  GameTypeID: number;
  GameDate: string;
  GameDateOffset: string;
  GameKey: number;
  GameGUID: string;
  GameScheduleNum: number | null;
}

interface ICfbGameDataV2 {
  homeId: number;
  awayId: number;
  week: number;
  gameDate: string;
  gameKey: number;
  gameGuid: string;
  gameScheduleNum: number | null;
}

class CfbApp extends SchedulePickerBase {
  ConferenceTeams: ConferenceTeamDictionary;
  TrackedTeams: { key: string; value: Record<string, Record<string, KnockoutObservable<ITeam>>> }[];
  Teams: { key: string; value: Record<string, Record<string, KnockoutObservable<ITeam>>> }[];
  Dict: TeamDictionary;
  Div1Top: KnockoutObservable<any>;
  Div2Top: KnockoutObservable<any>;
  RCFBStyles: KnockoutObservable<boolean>;
  Warning: KnockoutObservable<string>;
  Info: KnockoutObservable<string>;
  SkipDivisionRank: KnockoutObservable<boolean>;
  SeasonYearEnd = 2017;
  PrimeTimeList: KnockoutObservableArray<CfbGameType>;
  HtmlExport: KnockoutObservable<any>;
  Export: KnockoutObservable<any>;
  TeamsOnBye: KnockoutComputed<any>;
  TreatNonPlayoffTeamsLikeWildcards: KnockoutObservable<boolean>;
  DraftOrder: KnockoutObservableArray<KnockoutObservable<CfbTeam>>;
  HideStatus: KnockoutObservable<boolean>;
  IsDraftOrderRanking: KnockoutObservable<boolean>;
  Version: number;
  GameScheduleGameMap: GameScheduleMap = {};
  NumGames: KnockoutObservable<number>;
  ShowSaveString: KnockoutObservable<boolean>;
  DraftTrades: KnockoutObservableArray<CfbDraftTrade>;
  DraftTradesMap: DraftTradeMap = {};
  Timeout: ReturnType<typeof setTimeout> | undefined;
  gameMap: Record<string | number, any> = {};

  private cfg: AppConfig;

  constructor() {
    const config = readConfig();
    super('CFBfootball', config);
    const self = this;
    self.cfg = config;

    self.DraftTrades = ko.observableArray([]);
    self.ShowSaveString = ko.observable(!!CfbApp.getQueryVariable('showString'));
    self.Hash = ko.observable(window.location.hash);
    self.NumGames = ko.observable(config.numGames ?? 0);
    self.IsDraftOrderRanking = ko.observable(false);
    self.MaxWeek(config.numWeeks ?? 0);
    self.Warning = ko.observable<string>('');
    self.Info = ko.observable<string>('');
    self.HtmlExport = ko.observable('');
    self.Export = ko.observable('');
    self.EmptySeasonString = config.emptySeasonString ?? '';

    self.HideStatus = ko.observable(userConfigManager.UserConfig.CFB?.HideStatus ?? false);
    self.HideStatus.subscribe((newVal: boolean) => {
      if (userConfigManager.UserConfig.CFB && newVal !== userConfigManager.UserConfig.CFB.HideStatus) {
        userConfigManager.UserConfig.CFB.HideStatus = newVal;
        userConfigManager.saveStorage();
      }
    });

    self.SaveString.subscribe(() => {
      self.setUrlRoute();
    });

    const handleLeagueData = (
      leagueName: string,
      seasonYear: number,
      currentWeekString: string,
      isCustomLeague: boolean,
      teamsList: any[],
      leagueGames: ICfbGameData[],
      replaceTeams: any[],
    ) => {
      if (seasonYear) { self.SeasonYearEnd = seasonYear; }
      if (currentWeekString) { self.CurrentSeasonString(currentWeekString); }
      self.LeagueName(leagueName);
      self.IsCustomLeague(isCustomLeague);
      const gamesEnumerable = Enumerable.From(leagueGames);
      self.MaxWeek(gamesEnumerable.Max((p: ICfbGameData) => p.Week));
      self.NumGames(leagueGames.length);
      self.TotalWeeks = self.MaxWeek();

      if (self.cfg.conferenceName === 'Big Ten') {
        self.TotalWeeks++;
      }

      const dict: TeamDictionary = {};
      dict['Mega'] = { 'Mega': {} as any };
      self.ConferenceTeams = {} as ConferenceTeamDictionary;
      self.ConferenceTeams['Mega'] = { 'Mega': {} as any };

      for (const team of teamsList) {
        if (!dict[team.Conference]) {
          dict[team.Conference] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
        }
        if (!dict[team.Conference][team.Division]) {
          dict[team.Conference][team.Division] = {} as Record<string, KnockoutObservable<ITeam>>;
        }
        if (!self.ConferenceTeams[team.Conference]) {
          self.ConferenceTeams[team.Conference] = {} as Record<string, KnockoutObservable<ITeam>>;
        }
        if (!self.ConferenceTeams[team.Conference][team.Division]) {
          self.ConferenceTeams[team.Conference][team.Division] = {} as unknown as KnockoutObservable<ITeam>;
        }
        const teamObj = ko.observable(new CfbTeam(
          team.TeamAbbreviation, team.RegionName, team.TeamName,
          team.Conference, team.Division, team.TeamID,
          team.ProgramName, team.TeamLogo,
        ));

        if (self.cfg.div1 === 'Mega') {
          (self.ConferenceTeams['Mega']['Mega'] as any)[team.TeamID] = teamObj;
          (dict['Mega']['Mega'] as any)[team.TeamID] = teamObj;
        } else {
          (dict[team.Conference][team.Division] as any)[team.TeamID] = teamObj;
          (self.ConferenceTeams[team.Conference][team.Division] as any)[team.TeamID] = teamObj;
        }
      }

      self.Dict = dict;
      self.Teams = mapDictionaryToArray(self.Dict);
      self.Weeks = Information.GetSchedule(self.Dict, leagueGames as any, self.cfg);

      if (self.cfg.conferenceName === 'Big Ten') {
        self.Weeks[self.MaxWeek() + 1] = new Array<CfbWeek>(new CfbWeek('Games'));
      }

      if (self.cfg.div1 === 'Mega') {
        for (const teamKey in dict['Mega']['Mega']) {
          const team = dict['Mega']['Mega'][teamKey];
          if (team().Schedule().length < 4) {
            delete (dict['Mega']['Mega'] as any)[teamKey];
            delete (self.ConferenceTeams['Mega']['Mega'] as any)[teamKey];
          }
        }
        self.TrackedTeams = Enumerable.From(mapDictionaryToArray(self.ConferenceTeams['Mega']['Mega'] as any))
          .Where((p: any) => p.value().Schedule().length > 3).ToArray();
      }

      if (gamesEnumerable.All((p: ICfbGameData) => p.GameScheduleNum != null)) {
        self.Version = 2;
        for (const weekIdx in self.Weeks) {
          const week = self.Weeks[weekIdx];
          const games = week[0].games();
          for (const gameObs of games) {
            const game = gameObs();
            self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
          }
        }
      } else {
        self.Version = 1;
      }

      self.SelectedWeek({ key: 1, value: self.Weeks[1] });
      self.NumGames(leagueGames.length);
      self.setGameScheduleNumbers(self.NumGames());

      self.Warning.subscribe((_newVal) => {
        // Bootstrap 5 alert dismiss is handled via data-bs-dismiss attributes natively
      });
      self.Div1Top = ko.observable([]);
      self.Div2Top = ko.observable([]);
      self.RCFBStyles = ko.observable<boolean>(false);
      self.SkipDivisionRank = ko.observable<boolean>(false);
      self.init(replaceTeams);

      if (self.Hash()) {
        try {
          const team = self.findTeam(self.Hash().slice(1))();
          self.showTeamStats(team);
          self.IsTeamSelected(true);
        } catch (ex) {
          console.warn(ex);
        }
      }

      self.UrlString.subscribe((newString) => {
        if (newString) {
          newString = newString.replace(/%20/gi, '+');
          const decompress = LZString.decompressFromEncodedURIComponent(newString);
          if (decompress) {
            self.load(decompress);
          }
          self.setUrlRoute();
        } else {
          self.setUrlRoute(true);
        }
      });

      if (self.LoadedWatchlist()) {
        self.UrlString(self.LoadedWatchlist().SaveString());
      } else {
        self.UrlString(
          SchedulePickerBase.getQueryVariable('L') || ''
          || localStorage.getItem(self.cfg.leagueGuid) || self.CurrentSeasonString(),
        );
      }

      const contentEl = document.getElementById('content');
      if (contentEl) {
        contentEl.querySelectorAll('.hidden').forEach((el) => (el as HTMLElement).classList.remove('hidden'));
      }
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.style.display = 'none'; }
      const navbars = document.querySelectorAll<HTMLElement>('.navbar');
      setTimeout(() => {
        if (self.cfg.conferenceName === 'Mega') {
          const navbarHeaders = document.querySelectorAll<HTMLElement>('.navbar-header');
          const eastEl = document.getElementById('East');
          if (eastEl && navbars.length >= 2) {
            if (navbarHeaders.length >= 2 && navbarHeaders[1].offsetHeight === 0) {
              eastEl.style.top = (navbars[1].offsetHeight + navbars[0].offsetHeight + 10) + 'px';
            } else if (navbarHeaders.length >= 2) {
              eastEl.style.top = (navbars[1].offsetHeight + navbarHeaders[1].offsetHeight) + 'px';
            }
          }
        }
      }, 80);

      ko.applyBindings(self, document.getElementById('content'));
      if (navbars.length >= 2) {
        document.body.style.paddingTop = (navbars[0].offsetHeight + navbars[1].offsetHeight) + 'px';
      }
      // Bootstrap popovers: initialised via data-bs-toggle="popover" in Bootstrap 5 natively
      super.finishComputing();
      super.initTeamSubscribers();
    };

    // All official leagues load from V2 API
    const sportId = self.cfg.sportId;
    const leagueGuid = self.cfg.leagueGuid;
    (window as any).leagueGuid = leagueGuid;
    ppFetch<any>({
      url: `${getApimBaseUrl()}/LeagueEngine/leagues/${sportId}/${leagueGuid}/official`,
      method: 'GET',
    }).then((result) => {
      const teamById: Record<number, any> = {};
      result.teams.forEach((t: any) => { teamById[t.teamId] = t; });
      const v1Teams = result.teams.map((t: any) => ({
        TeamAbbreviation: t.teamAbbreviation, RegionName: t.regionName, TeamName: t.teamName,
        Conference: t.conference, Division: t.division, TeamID: t.teamId,
        ProgramName: t.programName, TeamLogo: t.teamLogo,
      }));
      const v1Games: ICfbGameData[] = result.league.games.map((g: ICfbGameDataV2) => {
        const home = teamById[g.homeId];
        const away = teamById[g.awayId];
        return {
          Home: { TeamAbbreviation: home.teamAbbreviation, RegionName: home.regionName, TeamName: home.teamName, Conference: home.conference, Division: home.division, TeamID: home.teamId, ProgramName: home.programName, TeamLogo: home.teamLogo },
          Away: { TeamAbbreviation: away.teamAbbreviation, RegionName: away.regionName, TeamName: away.teamName, Conference: away.conference, Division: away.division, TeamID: away.teamId, ProgramName: away.programName, TeamLogo: away.teamLogo },
          Week: g.week, GameTypeID: 0, GameDate: g.gameDate, GameDateOffset: g.gameDate,
          GameKey: g.gameKey, GameGUID: g.gameGuid, GameScheduleNum: g.gameScheduleNum,
        } as ICfbGameData;
      });
      handleLeagueData(result.league.leagueName, result.league.superbowlYear, result.league.currentWeekString, false, v1Teams, v1Games, []);
    }).catch((err: unknown) => {
      console.error('CFB schedule-picker load error:', err);
      const loadingEl = document.getElementById('loading');
      if (loadingEl) { loadingEl.textContent = 'An error occurred'; }
    });

    const bracketsEl = document.getElementById('brackets');
    if (bracketsEl) { bracketsEl.style.display = 'none'; }
    super.startComputing();
    exposeToWindow(self);
  }

  setUrlRoute(isEmptyString?: boolean) {
    const self = this;
    let route: string;
    if (isEmptyString) {
      route = `https://${location.host}/CFBFootball/${self.cfg.apiRoute}?L=${self.EmptySeasonString}`;
    } else {
      if (self.SaveString() === 'Aw18ZXTt-CJA' && self.cfg.div1 === 'Mega') {
        self.SaveString('Aw18ZXTt-DFOS1b0c17Pd-wYUcSaWeRZVRUA');
      }
      route = `https://${location.host}/CFBFootball/${self.cfg.apiRoute}?L=${self.SaveString()}`;
    }
    self.UrlRoute(route);
  }

  resetWeek() {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of week.games()) {
        const game = gameObs() as CfbGame;
        if (game.selectionMade()) {
          self.selectGame(gameObs, game.TeamThatWon(), SelectionType.NONE, true);
        }
      }
    }
    self.UpdateStats(-1);
  }

  randomize() {
    const self = this;
    if (confirm('All of your regular season selections will be randomized, replacing your current selections. Are you sure you want to proceed?')) {
      for (let i = 0; i < self.NumGames(); i++) {
        let selection = 0;
        for (let j = 0; j < 4; j++) {
          selection = Math.floor(Math.random() * 2) + 1;
        }
        self.GameArray[i] = selection;
      }
      self.updateSaveString();
      self.load(self.GameArray.join(''));
      for (const team of self.AllTeams()) {
        team().FakeObs.valueHasMutated();
        (team() as CfbTeam).Wins.valueHasMutated();
        (team() as CfbTeam).Losses.valueHasMutated();
        (team() as CfbTeam).Ties.valueHasMutated();
      }
      self.UpdateStats(-1);
      self.SelectedWeek({ key: self.MaxWeek(), value: self.Weeks[self.MaxWeek()] });
    }
  }

  randomizeWeek() {
    const self = this;
    for (const week of self.SelectedWeek().value) {
      for (const gameObs of week.games()) {
        const game = gameObs() as CfbGame;
        self.unselectGame(gameObs, true);
        let selection = 0;
        if (self.SelectedWeek().key <= 17) {
          for (let j = 0; j < 4; j++) {
            selection = Math.floor(Math.random() * 2) + 1;
          }
        } else {
          selection = Math.floor(Math.random() * 2) + 1;
        }
        let teamSelected: any;
        switch (selection) {
          case SelectionType.AWAY_WIN:
            teamSelected = game.away;
            break;
          case SelectionType.HOME_WIN:
            teamSelected = game.home;
            break;
          default:
            teamSelected = game.home;
            break;
        }
        self.selectGame(gameObs, teamSelected, selection, true);
        (game.TeamThatWon()() as CfbTeam).GameRecords.valueHasMutated();
        (game.TeamThatLost()() as CfbTeam).GameRecords.valueHasMutated();
      }
    }
    self.UpdateStats(-1);
  }

  convertWeekKeyToName(keyStr: string) {
    const key = parseInt(keyStr, 10);
    if (this.cfg.conferenceName === 'Big Ten') {
      if (key === this.MaxWeek() + 1) {
        return 'Championship Game';
      }
    }
    return key.toString();
  }

  init(replaceTeams?: any[]) {
    const self = this;
    const allTeams: KnockoutObservable<ITeam>[] = [];
    let div1Teams: Record<string, KnockoutObservable<ITeam>[]> = {};
    let div2Teams: Record<string, KnockoutObservable<ITeam>[]> = {};

    if (self.cfg.div1 === 'Mega') {
      Object.keys(self.ConferenceTeams).forEach((confName) => {
        Object.keys(self.ConferenceTeams[confName]).forEach((divName) => {
          Object.assign(div1Teams, self.ConferenceTeams[confName][divName]);
        });
      });
    } else {
      div1Teams = self.ConferenceTeams[self.cfg.conferenceName][self.cfg.div1] as any;
      div2Teams = self.ConferenceTeams[self.cfg.conferenceName][self.cfg.div2] as any;
    }

    for (const teamInd in div1Teams) {
      const team = div1Teams[teamInd];
      if (ko.isObservable(team)) {
        allTeams.push(team as KnockoutObservable<ITeam>);
      } else {
        allTeams.push(ko.observable(new CfbTeam(
          (team as any).TeamAbbreviation, (team as any).RegionName, (team as any).TeamName,
          (team as any).Conference, (team as any).Division, (team as any).TeamID,
          (team as any).ProgramName, (team as any).TeamLogo,
        )));
      }
    }

    if (self.cfg.numDivisions === 2) {
      for (const teamInd in div2Teams) {
        const team = div2Teams[teamInd];
        if (ko.isObservable(team)) {
          allTeams.push(team as KnockoutObservable<ITeam>);
        } else {
          allTeams.push(ko.observable(new CfbTeam(
            (team as any).TeamAbbreviation, (team as any).RegionName, (team as any).TeamName,
            (team as any).Conference, (team as any).Division, (team as any).TeamID,
            (team as any).ProgramName, (team as any).TeamLogo,
          )));
        }
      }
    }

    if (self.cfg.numDivisions !== -1) {
      for (const gameIdx in self.GameScheduleGameMap) {
        const game = self.GameScheduleGameMap[gameIdx]();
        if ((game.away() as CfbTeam).Conference !== self.cfg.conferenceName) {
          const team = game.away() as CfbTeam;
          if (allTeams.filter((p: any) => team.TeamID === p().TeamID).length === 0) {
            allTeams.push(ko.observable(new CfbTeam(
              team.TeamAbbreviation, team.RegionName, team.TeamName,
              team.Conference, team.Division, team.TeamID, team.ProgramName, team.TeamLogo,
            )));
          }
        }
        if ((game.home() as CfbTeam).Conference !== self.cfg.conferenceName) {
          const team = game.home() as CfbTeam;
          if (allTeams.filter((p: any) => team.TeamID === p().TeamID).length === 0) {
            allTeams.push(ko.observable(new CfbTeam(
              team.TeamAbbreviation, team.RegionName, team.TeamName,
              team.Conference, team.Division, team.TeamID, team.ProgramName, team.TeamLogo,
            )));
          }
        }
      }
    }

    self.AllTeams(allTeams);

    if (replaceTeams) {
      for (const teamOverride of replaceTeams) {
        const teamReplaced: any = Enumerable.From(self.AllTeams()).First((p: any) => p().TeamID === teamOverride.TeamReplaced);
        self.AllTeams.remove(teamReplaced);
        teamReplaced().RegionName = teamOverride.RegionName;
        teamReplaced().TeamNameObs(teamOverride.TeamName);
        teamReplaced().TeamName = teamOverride.TeamName;
        teamReplaced().Conference = teamOverride.Conference;
        teamReplaced().Division = teamOverride.Division;
        self.AllTeams.push(teamReplaced);
      }
    }

    self.SelectedTeam.subscribe((newVal) => {
      if (newVal) {
        self.IsTeamSelected(true);
      }
    });
  }

  unselectGame(gameInput: KnockoutObservable<IGame>, skipUpdate?: boolean) {
    const self = this;
    gameInput().selectionMade(false);
    gameInput().teamSelected('');
    self.GameArray[gameInput().gameScheduleNum()] = 0;
    self.updateSaveString();
    if (!skipUpdate) {
      self.UpdateStats(-1);
    }
  }

  load(urlString: string) {
    const self = this;

    if (self.GameArray.length !== urlString.length) {
      self.setGameScheduleNumbers(urlString.length);
    }
    self.BlockHistoryPush(true);
    let biggestWeek = 1;

    if (self.IsAfterFirstLoad) {
      for (const weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
        });
      }
      const bracketsEl = document.getElementById('brackets');
      if (bracketsEl) { bracketsEl.innerHTML = ''; }
      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        (team as CfbTeam).reset();
        team.FakeObs();
      }
    }
    self.IsAfterFirstLoad = true;
    self.initTeamSubscribers();

    for (let i = 0; i <= urlString.length; i++) {
      const selection: SelectionType = parseInt(urlString[i], 10) as SelectionType;
      const skipUpdate = i <= self.NumGames() - 1;

      if (i === self.NumGames()) {
        self.UpdateStats(i);
        self.SeedConfCg();
      }

      const game = self.GameScheduleGameMap[i];
      if (!game) continue;
      const _game = game();
      const home = _game.home() as CfbTeam;
      const away = _game.away() as CfbTeam;
      const weekInd = _game.week;

      const input = {
        gameScheduleNum: _game.gameScheduleNum(),
        home: { Conference: home.Conference, Division: home.Division, TeamName: home.TeamNameObs(), Pct: home.GameRecords.AllRecord.Pct },
        away: { Conference: away.Conference, Division: away.Division, TeamName: away.TeamNameObs(), Pct: away.GameRecords.AllRecord.Pct },
        isConferenceGame: _game.isConferenceGame,
      } as unknown as IGame;

      switch (selection) {
        case SelectionType.AWAY_WIN:
          self.GameArray[i] = SelectionType.AWAY_WIN;
          if (biggestWeek < +weekInd) biggestWeek = +weekInd;
          if (game().gameScheduleNum() <= self.NumGames() - 1) {
            away.Wins.push(input);
            home.Losses.push(input);
          }
          self.gameSelected(game, game().away, selection, i, skipUpdate);
          break;
        case SelectionType.HOME_WIN:
          self.GameArray[i] = SelectionType.HOME_WIN;
          if (biggestWeek < +weekInd) biggestWeek = +weekInd;
          if (game().gameScheduleNum() <= self.NumGames() - 1) {
            away.Losses.push(input);
            home.Wins.push(input);
          }
          self.gameSelected(game, game().home, selection, i, skipUpdate);
          break;
        case SelectionType.NONE:
          if (game().selectionType() !== SelectionType.NONE) {
            self.unselectGame(game, skipUpdate);
          }
          self.GameArray[i] = SelectionType.NONE;
          break;
      }
      self.gameMap[game().gameKey] = { position: i, result: 0 };
    }

    self.UpdateStats(-1);

    const week = CfbApp.getQueryVariable('week');
    if (week) {
      const weekParsed = parseInt(week, 10);
      self.switchContext({ key: weekParsed, value: self.Weeks[weekParsed] });
      self.BiggestWeek(weekParsed);
    } else {
      self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
      self.BiggestWeek(biggestWeek);
    }

    if (self.Hash()) {
      try {
        let team: any = self.findTeam(self.Hash().slice(1));
        if (ko.isObservable(team)) team = team();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }

    self.BlockHistoryPush(true);
    self.updateSaveString();
    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  pushHistory() {
    super.pushHistory();
  }

  selectGame(gameInput: KnockoutObservable<IGame>, selectedTeam: any, selectionType: SelectionType, isRandomize = false) {
    const self = this;
    const _gameInput = gameInput();
    const home = _gameInput.home() as CfbTeam;
    const away = _gameInput.away() as CfbTeam;
    const input = {
      gameScheduleNum: _gameInput.gameScheduleNum(),
      home: { Conference: home.Conference, Division: home.Division, TeamName: home.TeamNameObs(), Pct: home.GameRecords.AllRecord.Pct },
      away: { Conference: away.Conference, Division: away.Division, TeamName: away.TeamNameObs(), Pct: away.GameRecords.AllRecord.Pct },
      isConferenceGame: _gameInput.isConferenceGame,
    } as unknown as IGame;

    if (typeof selectedTeam === 'function' && selectedTeam().SelectedCss() === _gameInput.teamSelected()) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = SelectionType.NONE;

      const gsn = (input as any).gameScheduleNum;
      home.Wins.remove((p: IGame) => p.gameScheduleNum() === gsn);
      away.Wins.remove((p: IGame) => p.gameScheduleNum() === gsn);
      home.Losses.remove((p: IGame) => p.gameScheduleNum() === gsn);
      away.Losses.remove((p: IGame) => p.gameScheduleNum() === gsn);
      home.Ties.remove((p: IGame) => p.gameScheduleNum() === gsn);
      away.Ties.remove((p: IGame) => p.gameScheduleNum() === gsn);

      home.Wins.valueHasMutated();
      home.Losses.valueHasMutated();
      home.Ties.valueHasMutated();
      away.Wins.valueHasMutated();
      away.Losses.valueHasMutated();
      away.Ties.valueHasMutated();

      clearTimeout(self.Timeout);
      self.Timeout = setTimeout(() => {
        self.updateSaveString();
        self.UpdateRankings(self.cfg.div1, -1);
        if (self.cfg.numDivisions === 2) {
          self.UpdateRankings(self.cfg.div2, -1);
          self.SeedConfCg();
        }
        self.pushHistory();
        super.finishComputing();
      }, 500);
    } else {
      self.GameArray[_gameInput.gameScheduleNum()] = selectionType;
      _gameInput.selectionMade(true);
      _gameInput.selectionType(selectionType);

      switch (selectionType) {
        case SelectionType.AWAY_WIN:
          _gameInput.teamSelected(away.SelectedCss());
          break;
        case SelectionType.HOME_WIN:
          _gameInput.teamSelected(home.SelectedCss());
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('');
          break;
        case SelectionType.TIE:
          _gameInput.teamSelected('TIE');
          break;
      }

      // Prevent records from updating for post-season games
      if (_gameInput.week < (self.MaxWeek() + 1)) {
        switch (selectionType) {
          case SelectionType.AWAY_WIN:
            _gameInput.teamSelected(away.SelectedCss());
            away.Wins.push(input);
            home.Losses.push(input);
            home.Losses.valueHasMutated();
            away.Wins.valueHasMutated();
            break;
          case SelectionType.HOME_WIN:
            _gameInput.teamSelected(home.SelectedCss());
            home.Wins.push(input);
            away.Losses.push(input);
            away.Losses.valueHasMutated();
            home.Wins.valueHasMutated();
            break;
        }
      }
    }

    // Big Ten has its own championship week; others: increment compareWindow by 1
    let compareWindow = self.MaxWeek();
    if (self.cfg.conferenceName !== 'Big Ten') {
      compareWindow++;
    }

    if (_gameInput.week < compareWindow) {
      clearTimeout(self.Timeout);
      self.Timeout = setTimeout(() => {
        self.UpdateStats(-1);
        self.SeedConfCg();
        if (self.IsTeamSelected()) {
          self.SelectedTeam().Schedule(self.getScheduleForTeam(self.SelectedTeam()));
        }
        super.finishComputing();
      }, 500);
    } else {
      self.updateSaveString();
      self.pushHistory();
    }
  }

  gameSelected(gameInput: KnockoutObservable<IGame>, selectedTeam: any, selectionType: SelectionType, positionCounter: number, skipUpdate?: boolean) {
    const self = this;
    gameInput().teamSelected(selectedTeam().SelectedCss());
    gameInput().selectionMade(true);
    gameInput().selectionType(selectionType);
    gameInput.valueHasMutated();
    if (!skipUpdate) {
      self.UpdateStats(positionCounter);
    }
  }

  UpdateRankings(divisionName: string, position: number) {
    const self = this;
    const teams: KnockoutObservable<ITeam>[] = [];
    const teamsInDivision = self.Dict[self.cfg.conferenceName][divisionName];

    Object.keys(teamsInDivision).forEach((teamKey) => {
      teamsInDivision[teamKey]().Tiebreakers({ HTH: [], Sweeps: [], DivHTH: [] });
      teams.push(teamsInDivision[teamKey]);
    });

    const teamsSorted = self.RankDivisionTeams(Enumerable.From(teams).ToArray());

    ko.utils.arrayForEach(teamsSorted, (team: KnockoutObservable<CfbTeam>, index: number) => {
      team().ConferenceRank(index + 1);
    });

    if (divisionName === self.cfg.div1) {
      // For single-division Big Ten, store the top two teams so SeedConfCg can
      // wire them as the championship game participants without needing a Div2.
      if (self.cfg.conferenceName === 'Big Ten' && self.cfg.numDivisions !== 2) {
        self.Div1Top(teamsSorted.slice(0, 2));
      } else {
        self.Div1Top([teamsSorted[0]]);
      }
    } else {
      self.Div2Top([teamsSorted[0]]);
    }
  }

  SeedConfCg() {
    const self = this;
    if (self.cfg.conferenceName === 'Big Ten') {
      const isSingleDivision = self.cfg.numDivisions !== 2;
      if (isSingleDivision) {
        // Single-division Big Ten (2024+): top two from the one division meet in the CG.
        if (self.Div1Top().length >= 2) {
          const cg = ko.observable(new CfbGame(self.Div1Top()[0], self.Div1Top()[1], self.MaxWeek() + 1));
          const gameNum = self.NumGames();
          cg().gameScheduleNum(gameNum);
          self.gameMap[gameNum] = cg;
          self.GameArray[gameNum] = 0;
          self.GameScheduleGameMap[gameNum] = cg;
          self.Weeks[self.MaxWeek() + 1][0].games([cg]);
        } else {
          self.Weeks[self.MaxWeek() + 1][0].games([]);
        }
      } else {
        // Two-division Big Ten (pre-2024): East winner vs West winner.
        if (self.Div1Top().length && self.Div2Top().length) {
          const cg = ko.observable(new CfbGame(self.Div1Top()[0], self.Div2Top()[0], self.MaxWeek() + 1));
          const gameNum = self.NumGames();
          cg().gameScheduleNum(gameNum);
          self.gameMap[gameNum] = cg;
          self.GameArray[gameNum] = 0;
          self.GameScheduleGameMap[gameNum] = cg;
          self.Weeks[self.MaxWeek() + 1][0].games([cg]);
        } else {
          self.Weeks[self.MaxWeek() + 1][0].games([]);
        }
      }
    }
  }

  HtHDivision(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const teamIdToNameDict: Record<number, string> = {};
    teams.forEach((team: any) => {
      teamIdToNameDict[team().TeamID] = team().TeamAbbreviation;
    });

    const hth_games: Record<string, number[]> = {};
    for (const Team in teams) {
      const left = teams[Team];
      hth_games[left().TeamID] = [0, 0, 0];
      for (const team2 in teams) {
        if (teams[Team] !== teams[team2]) {
          const right = teams[team2];
          hth_games[right().TeamID] = [0, 0, 0];
        }
      }
    }

    const gamesSeen: Record<string, boolean> = {};
    for (const Team in teams) {
      const left = teams[Team];
      for (const team2 in teams) {
        if (teams[Team] !== teams[team2]) {
          const right = teams[team2];
          const hthGames = this.getHTHDivisionGames(left, right);
          if (hthGames.length > 0) {
            if (hthGames[0]().selectionMade) {
              const game = hthGames[0];
              if (gamesSeen[game().gameScheduleNum()]) continue;
              gamesSeen[game().gameScheduleNum()] = true;
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamID === left().TeamID) {
                hth_games[left().TeamID][0]++;
                hth_games[right().TeamID][1]++;
              }
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamID === right().TeamID) {
                hth_games[right().TeamID][0]++;
                hth_games[left().TeamID][1]++;
              }
            }
            if (hthGames[1] && hthGames[1]().selectionMade) {
              const game = hthGames[1];
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamID === left().TeamID) {
                hth_games[left().TeamID][0]++;
                hth_games[right().TeamID][1]++;
              }
              if (game().TeamThatWon() && game().TeamThatWon()() && game().TeamThatWon()().TeamID === right().TeamID) {
                hth_games[right().TeamID][0]++;
                hth_games[left().TeamID][1]++;
              }
            }
          }
        }
      }
    }

    for (const team in teams) {
      (teams[team] as any).HtHDivCalcPct = this.CalcPct(hth_games[(teams[team]() as any).TeamID]);
    }

    const groups = Enumerable.From(teams).OrderByDescending((p: any) => p.HtHDivCalcPct).GroupBy((p: any) => p.HtHDivCalcPct).ToArray();
    const remaining = groups[0].source;

    if (groups.length > 1) {
      const hthbeaten: string[] = [];
      const temp: string[] = [];
      ko.utils.arrayForEach(Enumerable.From(groups).ToArray(), (item: any) => {
        ko.utils.arrayForEach(item.source, (innerItem: any) => {
          hthbeaten.push(innerItem().TeamID);
        });
      });
      ko.utils.arrayForEach(remaining, (teamRem: any) => {
        teamRem().Tiebreakers()['DivHTH'] = [];
      });
      ko.utils.arrayForEach(remaining, (teamRem: any) => {
        ko.utils.arrayForEach(hthbeaten, (teamId: string) => {
          temp.push(teamId);
        });
        const hthEnumerable = Enumerable.From(temp).Distinct().ToArray();
        for (const teamId of hthEnumerable) {
          teamRem().Tiebreakers()['DivHTH'].push(
            `${teamIdToNameDict[teamId as any]} (${hth_games[teamId][0]}-${hth_games[teamId][1]}${hth_games[teamId][2] ? ('-' + hth_games[teamId][2]) : ''})`,
          );
        }
        teamRem().Tiebreakers().DivHTH = Enumerable.From(teamRem().Tiebreakers().DivHTH).Distinct().ToArray();
        teamRem().Tiebreakers.valueHasMutated();
      });
    }

    return remaining;
  }

  RankDivisionTeams(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const top: KnockoutObservable<ITeam>[] = [];
    const remaining = teams.slice(0);
    for (const team of remaining) {
      team().RankMethod(-1);
    }
    while (remaining.length > 0) {
      const best = this.DivisionPickTop(remaining, null);
      top.push(best);
      remaining.splice(remaining.indexOf(best), 1);
    }
    return top;
  }

  DivisionPickTop(teams: KnockoutObservable<ITeam>[], preventRankOverride?: any): KnockoutObservable<ITeam> {
    if (!Enumerable.From(teams).Any((p: any) => p === undefined)) {
      let top: KnockoutObservable<ITeam>[] = [];

      if (this.cfg.div1 === 'Mega') {
        top = this.BestPctNaN(teams);
        if (top.length === 1) {
          if ((top[0] as any).SkipRanking === false || top[0]().RankMethod() === RankMethod.Unknown || (top[0]().RankMethod() <= 10 && top[0]().RankMethod() >= 5 && preventRankOverride !== true)) {
            top[0]().RankMethod(RankMethod.AllPCT);
          }
          return top[0];
        }
      } else {
        if (this.cfg.numDivisions === 2) {
          top = this.BestDivPctNaN(teams);
        } else {
          top = this.BestConfPct(teams);
        }
        if (top.length === 1) {
          if ((top[0] as any).SkipRanking === false || top[0]().RankMethod() === RankMethod.Unknown || (top[0]().RankMethod() <= 10 && top[0]().RankMethod() >= 5 && preventRankOverride !== true)) {
            if (this.cfg.numDivisions === 2) {
              top[0]().RankMethod(RankMethod.DivPCT);
            } else {
              top[0]().RankMethod(RankMethod.ConfPCT);
            }
          }
          return top[0];
        }
      }

      if (top.length === 1) {
        return top[0];
      } else {
        if (this.cfg.conferenceName === 'Big Ten') {
          return this.BigTenTiebreaker(top);
        } else {
          return this.DivisionTiebreaker(top);
        }
      }
    }
    return undefined;
  }

  BigTenTiebreaker(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam> {
    let remaining = teams.slice(0);

    if (Enumerable.From(remaining).All((p: any) => p().Wins() + p().Losses() === 0)) {
      return this.CoinFlip(remaining);
    }

    remaining = this.BestConfPctNaN(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.ConfPCT);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    remaining = this.BestDivPctNaN(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.DivPCT);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    remaining = this.HtHDivision(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.HTH);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    remaining = this.BestConfPctNaN(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.ConfPCT);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    remaining = this.BestCommonConferencePct(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.CommonGames);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    remaining = this.BestCumulativeWinningPercentageOfNonDivisionalOpponents(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.CumulativeWinningPercentageOfNonDivisionalOpponents);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.BigTenTiebreaker(remaining);
    }

    return this.CoinFlip(remaining);
  }

  DivisionTiebreaker(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam> {
    let remaining = teams.slice(0);

    if (Enumerable.From(teams).Any((p: any) => p === undefined)) {
      return undefined;
    }

    if (this.cfg.div1 !== 'Mega') {
      remaining = this.HtHConference(remaining);
      if (remaining.length === 1) {
        remaining[0]().RankMethod(RankMethod.HTH);
        return remaining[0];
      } else if (remaining.length !== teams.length) {
        return this.DivisionTiebreaker(remaining);
      }

      remaining = this.BestCommonPct(remaining);
      if (remaining.length === 1) {
        remaining[0]().Tiebreakers.valueHasMutated();
        remaining[0]().RankMethod(RankMethod.CommonPct);
        return remaining[0];
      } else if (remaining.length !== teams.length) {
        return this.DivisionTiebreaker(remaining);
      }
    }

    if (Enumerable.From(remaining).All((p: any) => p().Wins() + p().Losses() === 0)) {
      return this.CoinFlip(remaining);
    }

    remaining = this.BestPctNaN(remaining);
    if (remaining.length === 1) {
      if (remaining[0]().RankMethod() === -1) {
        remaining[0]().RankMethod(RankMethod.AllPCT);
      }
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.DivisionTiebreaker(remaining);
    }

    remaining = this.BestSOV(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.SOV);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.DivisionTiebreaker(remaining);
    }

    remaining = this.BestSOS(remaining);
    if (remaining.length === 1) {
      remaining[0]().RankMethod(RankMethod.SOS);
      return remaining[0];
    } else if (remaining.length !== teams.length) {
      return this.DivisionTiebreaker(remaining);
    }

    return this.CoinFlip(remaining);
  }

  CoinFlip(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam> {
    let coinFlipSum = 0;
    for (const team in teams) {
      coinFlipSum += Math.random() * Math.pow(2, 8);
    }
    const luckyTeam = teams[Math.floor(coinFlipSum % teams.length)];
    luckyTeam().RankMethod(RankMethod.CoinFlip);
    return luckyTeam;
  }

  HtHConference(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const games: Record<string, number> = {};
    for (const team of teams) {
      if (!team) break;
      for (const foe of teams.slice(0)) {
        if (!foe) break;
        if (team === foe) continue;
        const schedule = this.getGamesVsEachOther(team, foe);
        ko.utils.arrayForEach(schedule, (game: any) => {
          if (game().selectionMade()) {
            games[game().away().TeamID + '-' + game().home().TeamID] = game().selectionType();
          }
        });
      }
    }

    for (let i = 0; i < teams.length; i++) {
      const team = teams[i];
      if (!team) break;
      let sweepUnbroken = true;
      for (let j = 0; j < teams.length; j++) {
        const foe = teams[j];
        if (!foe) break;
        if (foe === team) continue;
        if (games[team().TeamID + '-' + foe().TeamID] !== SelectionType.AWAY_WIN && games[foe().TeamID + '-' + team().TeamID] !== SelectionType.HOME_WIN) {
          sweepUnbroken = false;
          break;
        }
      }
      if (sweepUnbroken) {
        const teamsSwept: string[] = [];
        ko.utils.arrayForEach(
          Enumerable.From(teams).Where((p: any) => p().TeamID !== team().TeamID).ToArray(),
          (item: any) => { teamsSwept.push(item().TeamID); },
        );
        team().Tiebreakers()['Sweeps'].push(...teamsSwept);
        return [team];
      }
    }

    for (let k = 0; k < teams.length; k++) {
      const team = teams[k];
      let sweptUnbroken = true;
      for (let l = 0; l < teams.length; l++) {
        const foe = teams[l];
        if (!team || !foe) break;
        if (foe !== team) {
          if (games[team().TeamID + '-' + foe().TeamID] !== SelectionType.HOME_WIN && games[foe().TeamID + '-' + team().TeamID] !== SelectionType.AWAY_WIN) {
            sweptUnbroken = false;
            break;
          }
        }
      }
      if (sweptUnbroken) {
        const remainingTeams = teams.slice(0);
        remainingTeams.splice(remainingTeams.indexOf(team), 1);
        return remainingTeams;
      }
    }

    return teams;
  }

  BestCommonConferencePct(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const commonRecord: Record<string, number[]> = {};
    const foes: string[] = [];
    const opponents: Record<string, any> = {};

    for (const team in teams) {
      opponents[teams[team]().TeamID] = { TeamsPlayed: [] };
      ko.utils.arrayPushAll(opponents[teams[team]().TeamID]['TeamsPlayed'], Enumerable.From(this.getConferenceOpponents(teams[team])).Distinct().ToArray());
      commonRecord[teams[team]().TeamID] = [0, 0, 0, 0];
    }

    const commonOpponents: Record<string, number> = {};
    for (const i in opponents) {
      for (const team in opponents[i].TeamsPlayed) {
        commonOpponents[opponents[i].TeamsPlayed[team]] = 0;
      }
    }
    for (const i in opponents) {
      for (const team in opponents[i].TeamsPlayed) {
        commonOpponents[opponents[i].TeamsPlayed[team]]++;
      }
    }

    let maxCommon = 0;
    for (const opponent in commonOpponents) {
      if (commonOpponents[opponent] > maxCommon) maxCommon = commonOpponents[opponent];
    }
    if (maxCommon === 1) return teams;

    for (const opponent in commonOpponents) {
      if (commonOpponents[opponent] === maxCommon && maxCommon !== 1) {
        foes.push(opponent);
      }
    }

    for (const _foe in foes) {
      const foeName = foes[_foe];
      for (const _team in teams) {
        const teamObs = teams[_team];
        const schedule = this.getScheduleForTeam(teamObs());
        const exists = ko.utils.arrayFirst(schedule, (game: any) => {
          if (game && foeName !== String(teamObs().TeamID)) {
            return game().home().TeamID === foeName || game().away().TeamID === foeName;
          }
        });
        if (!exists) return teams;
      }
    }

    for (const team in teams) {
      const sched = this.getScheduleForTeam(teams[team]());
      const TeamName = teams[team]().TeamID;
      const teamRec = commonRecord[TeamName];
      for (const foe in foes) {
        for (const gameInt in sched) {
          const game = sched[gameInt] != null ? sched[gameInt]() : false;
          if (game && game.selectionMade() && (game as CfbGame).BothTeamNamesInGame(String(TeamName), foes[foe])) {
            if (game.TeamThatWon()().TeamID === TeamName) teamRec[0]++;
            else if (game.TeamThatWon()().TeamID !== TeamName) teamRec[1]++;
          } else if (game && !game.selectionMade() && (game as CfbGame).BothTeamNamesInGame(String(TeamName), foes[foe])) {
            teamRec[3]++;
          }
        }
      }
    }

    for (const teamName in commonRecord) {
      const rec = commonRecord[teamName];
      if (rec[0] === 0 && rec[1] === 0 && rec[2] === 0) return teams;
    }

    for (const team in teams) {
      const teamRec = commonRecord[teams[team]().TeamID];
      if (teamRec[0] + teamRec[1] + teamRec[2] + teamRec[3] < 4) return teams;
      (teams as any)[team].BestCommonCalcPct = this.CalcPctNaN(teamRec);
    }

    const groups = Enumerable.From(teams).OrderByDescending((p: any) => p.BestCommonCalcPct).GroupBy((p: any) => p.BestCommonCalcPct).ToArray();
    const remaining = groups[0].source;

    if (groups.length > 1) {
      ko.utils.arrayForEach(remaining, (teamRem: any) => {
        teamRem().Tiebreakers()['CommonGames'] = commonRecord;
        teamRem().Tiebreakers()['Explanations'] = `Common opponents: ${foes.map((t) => ' ' + t)}`;
        teamRem().Tiebreakers.valueHasMutated();
      });
    }

    return remaining;
  }

  BestCommonPct(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const commonRecord: Record<string, number[]> = {};
    const foes: string[] = [];
    const opponents: Record<string, any> = {};

    for (const team in teams) {
      opponents[teams[team]().TeamID] = { TeamsPlayed: [] };
      ko.utils.arrayPushAll(opponents[teams[team]().TeamID]['TeamsPlayed'], Enumerable.From(this.getOpponents(teams[team])).Distinct().ToArray());
      commonRecord[teams[team]().TeamID] = [0, 0, 0, 0];
    }

    const commonOpponents: Record<string, number> = {};
    for (const i in opponents) {
      for (const team in opponents[i].TeamsPlayed) {
        commonOpponents[opponents[i].TeamsPlayed[team]] = 0;
      }
    }
    for (const i in opponents) {
      for (const team in opponents[i].TeamsPlayed) {
        commonOpponents[opponents[i].TeamsPlayed[team]]++;
      }
    }

    let maxCommon = 0;
    for (const opponent in commonOpponents) {
      if (commonOpponents[opponent] > maxCommon) maxCommon = commonOpponents[opponent];
    }
    if (maxCommon === 1) return teams;

    for (const opponent in commonOpponents) {
      if (commonOpponents[opponent] === maxCommon && maxCommon !== 1) {
        foes.push(opponent);
      }
    }

    for (const _foe in foes) {
      const foeName = foes[_foe];
      for (const _team in teams) {
        const teamObs = teams[_team];
        const schedule = this.getScheduleForTeam(teamObs());
        const exists = ko.utils.arrayFirst(schedule, (game: any) => {
          if (game && foeName !== String(teamObs().TeamID)) {
            return game().home().TeamID === foeName || game().away().TeamID === foeName;
          }
        });
        if (!exists) return teams;
      }
    }

    for (const team in teams) {
      const sched = this.getScheduleForTeam(teams[team]());
      const TeamName = teams[team]().TeamID;
      const teamRec = commonRecord[TeamName];
      for (const foe in foes) {
        for (const gameInt in sched) {
          const game = sched[gameInt] != null ? sched[gameInt]() : false;
          if (game && game.selectionMade() && (game as CfbGame).BothTeamNamesInGame(String(TeamName), foes[foe])) {
            if (game.TeamThatWon()().TeamID === TeamName) teamRec[0]++;
            else if (game.TeamThatWon()().TeamID !== TeamName) teamRec[1]++;
          } else if (game && !game.selectionMade() && (game as CfbGame).BothTeamNamesInGame(String(TeamName), foes[foe])) {
            teamRec[3]++;
          }
        }
      }
    }

    for (const teamName in commonRecord) {
      const rec = commonRecord[teamName];
      if (rec[0] === 0 && rec[1] === 0 && rec[2] === 0) return teams;
    }

    for (const team in teams) {
      const teamRec = commonRecord[teams[team]().TeamID];
      if (teamRec[0] + teamRec[1] + teamRec[2] + teamRec[3] < 4) return teams;
      (teams as any)[team].BestCommonCalcPct = this.CalcPctNaN(teamRec);
    }

    const groups = Enumerable.From(teams).OrderByDescending((p: any) => p.BestCommonCalcPct).GroupBy((p: any) => p.BestCommonCalcPct).ToArray();
    const remaining = groups[0].source;

    if (groups.length > 1) {
      ko.utils.arrayForEach(remaining, (teamRem: any) => {
        teamRem().Tiebreakers()['CommonGames'] = commonRecord;
        teamRem().Tiebreakers()['Explanations'] = `Common opponents: ${foes.map((t) => ' ' + t)}`;
        teamRem().Tiebreakers.valueHasMutated();
      });
    }

    return remaining;
  }

  BestCumulativeWinningPercentageOfNonDivisionalOpponents(teams: KnockoutObservable<ITeam>[]): KnockoutObservable<ITeam>[] {
    const teamDict: Record<string, number[]> = {};

    teams.forEach((teamObs) => {
      const unwrapped = teamObs() as CfbTeam;
      teamDict[unwrapped.TeamID] = [0, 0, 0];
      const schedule = this.getScheduleForTeam(unwrapped);
      schedule.forEach((game) => {
        if (!game) return;
        const awayUnwrapped = game().away() as CfbTeam;
        if (awayUnwrapped.TeamID !== unwrapped.TeamID) {
          if (awayUnwrapped.Division !== unwrapped.Division && awayUnwrapped.Conference === unwrapped.Conference) {
            teamDict[unwrapped.TeamID][0] += awayUnwrapped.GameRecords.ConferenceRecord.Wins();
            teamDict[unwrapped.TeamID][1] += awayUnwrapped.GameRecords.ConferenceRecord.Losses();
          }
        } else {
          const homeUnwrapped = game().home() as CfbTeam;
          if (homeUnwrapped.Division !== unwrapped.Division && homeUnwrapped.Conference === unwrapped.Conference) {
            teamDict[unwrapped.TeamID][0] += homeUnwrapped.GameRecords.ConferenceRecord.Wins();
            teamDict[unwrapped.TeamID][1] += homeUnwrapped.GameRecords.ConferenceRecord.Losses();
          }
        }
      });
    });

    teams.forEach((teamObs) => {
      (teamObs as any).BestCumulativeWinningPercentageOfNonDivisionalOpponents = this.CalcPctNaN(teamDict[(teamObs() as any).TeamID]);
    });

    const groups = Enumerable.From(teams)
      .OrderByDescending((p: any) => p.BestCumulativeWinningPercentageOfNonDivisionalOpponents)
      .GroupBy((p: any) => p.BestCumulativeWinningPercentageOfNonDivisionalOpponents)
      .ToArray();

    return groups[0].source;
  }

  getHTHDivisionGames(left: KnockoutObservable<ITeam>, right: KnockoutObservable<ITeam>): KnockoutObservable<IGame>[] {
    const leftSched = this.getScheduleForTeam(left());
    const rightSched = this.getScheduleForTeam(right());
    let allGames: KnockoutObservable<IGame>[] = [];
    ko.utils.arrayPushAll(allGames, leftSched);
    ko.utils.arrayPushAll(allGames, rightSched);
    allGames = ko.utils.arrayFilter(allGames, (game: KnockoutObservable<IGame>) => {
      if (game) {
        return (
          (game().home === left || game().home === right) &&
          (game().away === left || game().away === right) &&
          game().away().Division === game().home().Division &&
          game().away().Conference === game().home().Conference &&
          game().week <= 17
        );
      }
    });
    return ko.utils.arrayGetDistinctValues(allGames);
  }

  UpdateStats(position: number) {
    const self = this;
    self.updateSaveString();
    self.UpdateRankings(self.cfg.div1, position);
    if (self.cfg.numDivisions === 2) {
      self.UpdateRankings(self.cfg.div2, position);
    }
    self.pushHistory();
    self.setSOSandSOV();
    document.getElementById('content')?.querySelectorAll('.hidden').forEach((el) => (el as HTMLElement).classList.remove('hidden'));
    super.finishComputing();
  }

  setSOSandSOV() {
    const self = this;
    for (let i = 0; i < self.AllTeams().length; i++) {
      const team = self.AllTeams()[i]() as CfbTeam;
      const SOS = { Wins: 0, Losses: 0, Ties: 0 };
      const SOV = { Wins: 0, Losses: 0, Ties: 0 };
      team.Schedule(self.getScheduleForTeam(team));
      ko.utils.arrayForEach(team.Schedule(), (gameObs: any) => {
        if (gameObs && gameObs().week <= self.MaxWeek()) {
          const game = ko.unwrap(gameObs);
          let sovMultiplier = 0;
          const home = ko.unwrap(game.home) as CfbTeam;
          const away = ko.unwrap(game.away) as CfbTeam;
          const isHome = home.TeamNameObs() === team.TeamNameObs();
          if (game.selectionType() !== SelectionType.NONE) {
            if (game.teamSelected() === `${team.TeamAbbreviation}_${team.ProgramName}`) {
              sovMultiplier += 1;
            }
            if (isHome) {
              SOV.Wins += away.GameRecords.AllRecord.Wins() * sovMultiplier;
              SOV.Losses += away.GameRecords.AllRecord.Losses() * sovMultiplier;
              SOV.Ties += away.GameRecords.AllRecord.Ties() * sovMultiplier;
              SOS.Wins += away.GameRecords.AllRecord.Wins();
              SOS.Losses += away.GameRecords.AllRecord.Losses();
              SOS.Ties += away.GameRecords.AllRecord.Ties();
            } else {
              SOV.Wins += home.GameRecords.AllRecord.Wins() * sovMultiplier;
              SOV.Losses += home.GameRecords.AllRecord.Losses() * sovMultiplier;
              SOV.Ties += home.GameRecords.AllRecord.Ties() * sovMultiplier;
              SOS.Wins += home.GameRecords.AllRecord.Wins();
              SOS.Losses += home.GameRecords.AllRecord.Losses();
              SOS.Ties += home.GameRecords.AllRecord.Ties();
            }
          }
        }
      });
      team.SOS(self.calcWLTPct(SOS.Losses, SOS.Wins, SOS.Ties));
      team.SOV(self.calcWLTPct(SOV.Losses, SOV.Wins, SOV.Ties));
      team.SOSObj(SOS);
      team.SOVObj(SOV);
    }
  }

  UpdateStatsPostSeason() {
    const self = this;
    self.updateSaveString();
    self.pushHistory();
    document.getElementById('content')?.querySelectorAll('.hidden').forEach((el) => (el as HTMLElement).classList.remove('hidden'));
    super.finishComputing();
  }

  setCurrentSeason(reset: boolean) {
    super.setCurrentSeason(reset);
  }

  getConferenceOpponents(team: KnockoutObservable<ITeam> | ITeam): any[] {
    const unwrapped = ko.unwrap(team) as CfbTeam;
    const TeamName = unwrapped.TeamID;
    const teamConf = unwrapped.Conference;
    const sched = Enumerable.From(this.getScheduleForTeam(unwrapped)).Take(17).ToArray();
    return Enumerable.From(sched)
      .Where((p: any) => p != null && p().week <= (this.cfg.numWeeks ?? 17) && p().Conference === teamConf)
      .Select((p: any) => p().away().TeamID === TeamName ? p().home().TeamID : p().away().TeamID)
      .Where((p: any) => p !== TeamName)
      .Distinct().ToArray();
  }

  getOpponents(team: KnockoutObservable<ITeam> | ITeam): any[] {
    const unwrapped = ko.unwrap(team) as CfbTeam;
    const TeamName = unwrapped.TeamID;
    const sched = Enumerable.From(this.getScheduleForTeam(unwrapped)).Take(17).ToArray();
    return Enumerable.From(sched)
      .Where((p: any) => p != null && p().week <= (this.cfg.numWeeks ?? 17))
      .Select((p: any) => p().away().TeamID === TeamName ? p().home().TeamID : p().away().TeamID)
      .Where((p: any) => p !== TeamName)
      .Distinct().ToArray();
  }

  private calcWLTPct(losses: number, wins: number, ties: number): number {
    const denom = wins + losses + ties;
    if (denom === 0) return 0;
    return (wins + 0.5 * ties) / denom;
  }
}
